/**
 * Cookie Consent Manager - Alpine.js component
 * Handles cookie consent banner, preferences modal, and server-side synchronization
 */
function cookieConsentManager() {
    // Load default configuration as fallback
    const defaultDataElement = document.getElementById('cookie-consent-default-data');
    const defaultData = defaultDataElement ? JSON.parse(defaultDataElement.textContent) : {
        storageKey: 'cookie_consent',
        bannerMessage: 'This website uses cookies to improve your experience.',
        acceptText: 'ACCEPT ALL',
        rejectText: 'REJECT ALL',
        readMoreText: 'CUSTOMIZE',
        modalTitle: 'Cookie Preferences',
        modalDescription: 'We use cookies to enhance your browsing experience.',
        saveText: 'SAVE PREFERENCES',
        cookieExpiryDays: 365,
        policyVersion: '1.0',
        categories: [
            {
                id: 'essential',
                title: 'Essential Cookies',
                description: 'These cookies are necessary for the website to function.',
                always_active: true
            }
        ]
    };

    // Check if API data is loaded (will be populated by HTMX response)
    const apiDataElement = document.getElementById('cookie-consent-data');
    let data = defaultData;

    if (apiDataElement && apiDataElement.textContent.trim()) {
        try {
            data = JSON.parse(apiDataElement.textContent);
        } catch (e) {
            console.warn('Failed to parse API cookie data, using defaults:', e);
        }
    }

    return {
        showBanner: false,
        showModal: false,
        policyLoaded: false,
        policyLoadRequested: false,
        policyVersion: data.policyVersion || '1.0',
        cookieCategories: {
            essential: true,
            analytics: false,
            marketing: false,
            personalisation: false
        },

        init() {
            // Listen for policy loaded from server
            document.addEventListener('htmx:afterRequest', (e) => {
                if (e.detail.elt.id === 'cookie-policy-loader') {
                    this.handlePolicyResponse(e);
                }
            });

            // Listen for consent save response
            document.addEventListener('htmx:afterRequest', (e) => {
                if (e.detail.elt.id === 'cookie-consent-form') {
                    this.handleConsentSaveResponse(e);
                }
            });

            // Listen for global open preferences event
            window.addEventListener('open-cookie-preferences', () => {
                this.openCookieModal();
            });

            // Check local consent initially
            // Policy will be loaded lazily only if modal is opened
            this.checkConsent();
        },

        handlePolicyResponse(event) {
            if (event.detail.successful) {
                try {
                    const response = JSON.parse(event.detail.xhr.responseText);

                    if (response.success === 1 && response.data) {
                        const policyData = response.data.policy_content || response.data;

                        const mappedData = {
                            storageKey: policyData.storage_key || data.storageKey,
                            bannerMessage: policyData.banner?.message || data.bannerMessage,
                            acceptText: policyData.banner?.accept_text || data.acceptText,
                            rejectText: policyData.banner?.reject_text || data.rejectText,
                            readMoreText: policyData.banner?.read_more_text || data.readMoreText,
                            modalTitle: policyData.modal?.title || data.modalTitle,
                            modalDescription: policyData.modal?.description || data.modalDescription,
                            saveText: policyData.modal?.save_text || data.saveText,
                            cookieExpiryDays: policyData.cookie_expiry_days || data.cookieExpiryDays,
                            policyVersion: policyData.version || response.data.version || data.policyVersion,
                            categories: policyData.categories || data.categories
                        };

                        Object.assign(data, mappedData);
                        this.policyVersion = mappedData.policyVersion;

                        const apiDataElement = document.getElementById('cookie-consent-data');
                        if (apiDataElement) {
                            apiDataElement.textContent = JSON.stringify(mappedData);
                        }

                        this.policyLoaded = true;
                        console.log('Cookie policy loaded from server successfully');
                    }
                } catch (error) {
                    console.error('Failed to process cookie policy response:', error);
                    this.policyLoaded = true;
                }
            } else {
                console.warn('Cookie policy API failed, using default configuration');
                this.policyLoaded = true;
            }
        },

        handleConsentSaveResponse(event) {
            if (event.detail.successful) {
                try {
                    const response = JSON.parse(event.detail.xhr.responseText);

                    if (response.success === 1) {
                        console.log('Consent preferences saved to server successfully');
                    }
                } catch (error) {
                    console.error('Failed to process consent save response:', error);
                }
            } else {
                console.warn('Failed to save consent preferences to server');
            }
        },

        getData() {
            return data;
        },

        checkConsent() {
            const stored = siteStorage.get(data.storageKey);
            if (stored) {
                try {
                    const storedData = JSON.parse(stored);
                    const expiryTime = new Date(storedData.timestamp);
                    expiryTime.setDate(expiryTime.getDate() + data.cookieExpiryDays);

                    if (new Date() < expiryTime) {
                        this.cookieCategories = { ...this.cookieCategories, ...storedData.preferences };
                        this.showBanner = false;
                        this.applyCookieSettings();
                        return;
                    }
                } catch (e) {
                    console.warn('Error parsing cookie consent data:', e);
                    localStorage.removeItem(data.storageKey);
                }
            }

            this.showBanner = true;
            this.policyLoaded = true;
        },

        toggleCategory(categoryId) {
            this.cookieCategories[categoryId] = !this.cookieCategories[categoryId];
        },

        openCookieModal() {
            if (!this.policyLoadRequested) {
                this.loadPolicyFromServer();
            }
            this.showModal = true;
        },

        loadPolicyFromServer() {
            this.policyLoadRequested = true;
            const loader = document.getElementById('cookie-policy-loader');
            if (loader) {
                htmx.trigger(loader, 'load-cookie-policy');
            }
        },

        closeModal() {
            this.showModal = false;
        },

        acceptAll() {
            this.cookieCategories = {
                essential: true,
                analytics: true,
                marketing: true,
                personalisation: true
            };
            this.saveAndClose();
        },

        rejectAll() {
            this.cookieCategories = {
                essential: true,
                analytics: false,
                marketing: false,
                personalisation: false
            };
            this.saveAndClose();
        },

        savePreferences() {
            this.saveAndClose();
        },

        saveAndClose() {
            const consentData = {
                preferences: this.cookieCategories,
                timestamp: new Date().toISOString()
            };

            siteStorage.set(data.storageKey, JSON.stringify(consentData));
            this.applyCookieSettings();
            this.showBanner = false;
            this.saveToServer();
        },

        saveToServer() {
            window.cookieConsentData = {
                preferences: this.cookieCategories,
                policy_version: this.policyVersion,
                timestamp: new Date().toISOString()
            };

            const form = document.getElementById('cookie-consent-form');
            if (form) {
                htmx.trigger(form, 'save-consent-preferences');
            }
        },

        applyCookieSettings() {
            if (this.cookieCategories.analytics) {
                this.setCookie('analytics_consent', 'true', data.cookieExpiryDays);
            } else {
                this.deleteCookie('analytics_consent');
            }

            if (this.cookieCategories.marketing) {
                this.setCookie('marketing_consent', 'true', data.cookieExpiryDays);
            } else {
                this.deleteCookie('marketing_consent');
            }

            if (this.cookieCategories.personalisation) {
                this.setCookie('personalisation_consent', 'true', data.cookieExpiryDays);
            } else {
                this.deleteCookie('personalisation_consent');
            }

            this.setCookie('cookie_consent_set', 'true', data.cookieExpiryDays);

            window.dispatchEvent(new CustomEvent('cookieConsentUpdate', {
                detail: {
                    categories: this.cookieCategories,
                    timestamp: new Date().toISOString()
                }
            }));
        },

        setCookie(name, value, days) {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
        },

        deleteCookie(name) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
        }
    };
}

/**
 * Global function to open cookie preferences modal
 * Can be called from anywhere in the page (e.g., footer links)
 */
function openCookiePreferences() {
    window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
}

window.cookieConsentManager = cookieConsentManager;
window.openCookiePreferences = openCookiePreferences;
